import { getView } from '../../engine/prism.js';
import { useScene } from '../../state/scene.js';
import { useSceneView } from '../../state/scene-view.js';

/**
 * Scene tab shortcuts as data (`keys`: `Ctrl+Shift+V` style, matched against
 * `KeyboardEvent.key`), so they can move into the shared shortcut registry
 * unchanged. Viewport-only keys (numpad views, V vertex snapping, Alt+drag)
 * are handled by the engine while the canvas has focus.
 */
export interface SceneShortcut {
  id: string;
  keys: string;
  label: string;
  run: () => void;
}

export function frameSelection(): void {
  getView('scene')?.setOption('frameSelection', '');
}

const scene = () => useScene.getState();

export const SCENE_SHORTCUTS: SceneShortcut[] = [
  { id: 'scene.undo', keys: 'Ctrl+Z', label: 'Undo', run: () => scene().undo() },
  { id: 'scene.redo', keys: 'Ctrl+Y', label: 'Redo', run: () => scene().redo() },
  { id: 'scene.redo2', keys: 'Ctrl+Shift+Z', label: 'Redo', run: () => scene().redo() },
  { id: 'scene.copy', keys: 'Ctrl+C', label: 'Copy', run: () => void scene().copy() },
  { id: 'scene.paste', keys: 'Ctrl+V', label: 'Paste', run: () => void scene().paste() },
  { id: 'scene.pasteChild', keys: 'Ctrl+Shift+V', label: 'Paste as Child', run: () => void scene().paste({ asChild: true }) },
  { id: 'scene.duplicate', keys: 'Ctrl+D', label: 'Duplicate', run: () => void scene().duplicateSelection() },
  { id: 'scene.snap', keys: 'Ctrl+G', label: 'Toggle snapping', run: () => scene().setSnap(!scene().snap) },
  { id: 'scene.save', keys: 'Ctrl+S', label: 'Save scene', run: () => void scene().flush() },
  { id: 'scene.select', keys: 'Q', label: 'Select tool', run: () => scene().setTool('select') },
  { id: 'scene.move', keys: 'W', label: 'Move tool', run: () => scene().setTool('move') },
  { id: 'scene.rotate', keys: 'E', label: 'Rotate tool', run: () => scene().setTool('rotate') },
  { id: 'scene.scale', keys: 'R', label: 'Scale tool', run: () => scene().setTool('scale') },
  { id: 'scene.space', keys: 'X', label: 'Toggle local / global', run: () => scene().setSpace(scene().space === 'world' ? 'local' : 'world') },
  {
    id: 'scene.pivot',
    keys: 'Z',
    label: 'Toggle pivot / center',
    run: () => useSceneView.getState().setPivot(useSceneView.getState().pivot === 'pivot' ? 'center' : 'pivot'),
  },
  { id: 'scene.frame', keys: 'F', label: 'Frame selected', run: frameSelection },
  { id: 'scene.delete', keys: 'Delete', label: 'Delete', run: () => void scene().deleteSelection() },
  { id: 'scene.delete2', keys: 'Backspace', label: 'Delete', run: () => void scene().deleteSelection() },
  { id: 'scene.deselect', keys: 'Escape', label: 'Deselect', run: () => scene().select([]) },
];

/** `Ctrl+Shift+V` style key string of an event (Meta counts as Ctrl). */
export function keyString(e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'altKey'>): string {
  const parts: string[] = [];
  if (e.ctrlKey || e.metaKey) parts.push('Ctrl');
  if (e.shiftKey) parts.push('Shift');
  if (e.altKey) parts.push('Alt');
  const key = e.key.length === 1 ? e.key.toUpperCase() : e.key;
  parts.push(key);
  return parts.join('+');
}

export function findShortcut(e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'altKey'>): SceneShortcut | undefined {
  const k = keyString(e);
  return SCENE_SHORTCUTS.find((s) => s.keys === k);
}
